import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
// Note: localStorage is used for token persistence across page reloads.
// For higher security requirements, consider httpOnly cookie-based auth.
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('bhumi_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth APIs
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
};

// User Management APIs (Master Admin)
export const userAPI = {
  getAll: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  getRoles: () => api.get('/roles'),
};

// Guide APIs
export const guideAPI = {
  getAll: (params) => api.get('/guides', { params }),
  getBySlug: (slug) => api.get(`/guides/${slug}`),
  getPdf: (slug) => `${API_URL}/api/guides/${slug}/pdf`,
  create: (data) => api.post('/guides', data),
  update: (id, data) => api.put(`/guides/${id}`, data),
  delete: (id) => api.delete(`/guides/${id}`),
};

// Analytics APIs
export const analyticsAPI = {
  track: (event) => api.post('/analytics/track', event),
  getSummary: (days = 30) => api.get(`/analytics/summary?days=${days}`),
  getRecentActivity: (limit = 20) => api.get(`/analytics/recent-activity?limit=${limit}`),
};

// Service APIs
export const serviceAPI = {
  getAll: (params) => api.get('/services', { params }),
  getAllAdmin: () => api.get('/services', { params: { include_hidden: true } }),
  getById: (id) => api.get(`/services/${id}`),
  getBySlug: (slug) => api.get(`/services/by-slug/${slug}`),
  create: (data) => api.post('/services', data),
  update: (id, data) => api.put(`/services/${id}`, data),
  delete: (id) => api.delete(`/services/${id}`),
  toggleVerify: (id) => api.patch(`/services/${id}/verify`),
  toggleVisibility: (id) => api.patch(`/services/${id}/visibility`),
};

// Rating APIs
export const ratingAPI = {
  getByService: (serviceId) => api.get(`/services/${serviceId}/ratings`),
  getAllAdmin: (params) => api.get('/ratings/admin', { params }),
  create: (serviceId, data) => api.post(`/services/${serviceId}/ratings`, data),
  delete: (id) => api.delete(`/ratings/${id}`),
  toggleVisibility: (id) => api.patch(`/ratings/${id}/visibility`),
  updateStatus: (id, status) => api.patch(`/ratings/${id}/status?status=${status}`),
};

// Lead APIs
export const leadAPI = {
  getAll: () => api.get('/leads'),
  create: (data) => api.post('/leads', data),
  delete: (id) => api.delete(`/leads/${id}`),
};

// FAQ APIs
export const faqAPI = {
  getAll: (params) => api.get('/faqs', { params }),
  create: (data) => api.post('/faqs', data),
  update: (id, data) => api.put(`/faqs/${id}`, data),
  delete: (id) => api.delete(`/faqs/${id}`),
};

// Stats API
export const statsAPI = {
  get: () => api.get('/stats'),
};

// Settings API
export const settingsAPI = {
  getAll: () => api.get('/settings'),
  getPublic: () => api.get('/settings/public'),
  update: (data) => api.put('/settings', data),
  testEmail: () => api.post('/test-email'),
};

// Push Notification API
export const pushAPI = {
  subscribe: (subscription) => api.post('/push/subscribe', subscription),
  unsubscribe: (subscription) => api.delete('/push/unsubscribe', { data: subscription }),
};

// Seed API
export const seedAPI = {
  seed: () => api.post('/seed'),
};

// Blog APIs
export const blogAPI = {
  getAll: (params) => api.get('/blog', { params }),
  getBySlug: (slug) => api.get(`/blog/${slug}`),
  create: (data) => api.post('/blog', data),
  update: (id, data) => api.put(`/blog/${id}`, data),
  delete: (id) => api.delete(`/blog/${id}`),
};

// WhatsApp helper
export const openWhatsApp = (phone, message = '') => {
  const cleanPhone = phone.replace(/[^0-9]/g, '');
  const encodedMessage = encodeURIComponent(message);
  const url = `https://wa.me/${cleanPhone}${message ? `?text=${encodedMessage}` : ''}`;
  window.open(url, '_blank');
};

export default api;
